import { FC, useEffect, useRef, useState } from 'react'
import { useCombobox } from 'downshift'
import { Box, Icon, Input, InputGroup, InputRightElement, List, ListItem, Text, useColorModeValue } from '@chakra-ui/react'
import { RiArrowDownSFill, RiArrowUpSFill } from 'react-icons/ri'
import useResize from '../../../hooks/useResize'
import useScroll from '../../../hooks/useScroll'

const getOptionsFilter = (inputValue: string) => {
  const lowerCasedInputValue = inputValue.toLowerCase()

  return (option: string) => {
    return (
      !inputValue ||
      option.toLowerCase().includes(lowerCasedInputValue) ||
      option.toLowerCase().includes(lowerCasedInputValue)
    )
  }
}


interface ComboBoxProps {
  placeholder?: string;
  options: string[];
  value?: string;
  size?: 'sm' | 'md' | 'lg';
  onChange?: (value: string) => void;
  isDisabled?: boolean;
}

const ComboBox: FC<ComboBoxProps> = ({
  options,
  placeholder,
  value = null,
  onChange,
  size = 'md',
  isDisabled = false,
}) => {
  const [items, setItems] = useState(options)
  const ref = useRef<HTMLDivElement>(null)
  const scrollY = useScroll()
  const [, height] = useResize()

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    selectItem,
  } = useCombobox({
    onInputValueChange({ inputValue }) {
      setItems(options.filter(getOptionsFilter(inputValue || '')))
    },
    items,
    itemToString(item: string | null) {
      return item ? item : ''
    },
    onSelectedItemChange({ selectedItem }) {
      if (selectedItem && onChange) {
        onChange(selectedItem)
      }
    }
  })

  useEffect(() => {
    selectItem(value)
  }, [value, selectItem])

  useEffect(() => {
    setItems(options)
  }, [options])

  const bgSelected = useColorModeValue('gray.100', 'gray.600')
  const bgHover = useColorModeValue('gray.200', 'gray.500')

  const getBackgroundColor = (
    index: number,
    item: string,
    highlightedIndex: number,
    selectedItem: string | null,
  ) => {
    if (selectedItem === item) {
      return bgSelected
    }

    if (highlightedIndex === index) {
      return bgHover
    }

    return 'transparent'
  };

  useEffect(() => {
    if (ref.current) {
      const boundaries = ref.current.getBoundingClientRect()

      if ((boundaries.height + boundaries.y) > height) {
        ref.current.style.top = 'auto'
        ref.current.style.bottom = 'calc(100% + 1px)'
      } else {
        ref.current.style.top = 'calc(100% + 1px)'
        ref.current.style.bottom = 'auto'
      }
    }

  }, [ref, isOpen, height, scrollY])

  return (
    <Box position='relative' w='full'>
      <InputGroup size={size}>
        <Input
          isDisabled={isDisabled}
          placeholder={placeholder}
          {...getInputProps()}
        />
        <InputRightElement>
          <Icon as={isOpen ? RiArrowUpSFill : RiArrowDownSFill} />
        </InputRightElement>
      </InputGroup>
      <Box ref={ref} position='absolute' zIndex={99} width='full'>
        <List
          display={isOpen && items.length > 0 ? undefined : 'none'} py={2} {...getMenuProps()}
          borderRadius={4}
          boxShadow='md'
          height={200}
          overflowY='scroll'
          bg={useColorModeValue('white', 'gray.700')}
        >

          {isOpen &&
            items.map((item, index) => (
              <ListItem
                key={`${item}${index}`}
                bg={getBackgroundColor(index, item, highlightedIndex, selectedItem)}
                p={2}
                cursor='pointer'
                {...getItemProps({ item, index })}
              >
                <Text>{item}</Text>
              </ListItem>
            ))}
        </List>
      </Box>
    </Box>
  )
}

export default ComboBox
